import { icon } from '../../components/icons';
import { $, $$ } from '../../lib/dom';
import { validateGroupSelection, validateValue, type FieldRules } from './validation-rules';

export type FormControl = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

/** Shows (or clears) the inline error for an element that has a matching `#<id>-err` container. */
function showFieldError(el: HTMLElement, message: string): void {
  if (message) el.setAttribute('aria-invalid', 'true');
  else el.removeAttribute('aria-invalid');
  const container = document.getElementById(`${el.id}-err`);
  if (container) container.innerHTML = message ? `${icon('alert')}<span>${message}</span>` : '';
}

const cleanLabel = (text: string | null): string => (text ?? '').replace('*', '').replace('(optional)', '').trim();

export function fieldLabel(el: FormControl): string {
  if (el instanceof HTMLInputElement && el.type === 'checkbox' && el.name === 'consent') return 'Consent';
  const label = (el.id ? document.querySelector(`label[for="${el.id}"]`) : null) ?? el.closest('label');
  return label ? cleanLabel(label.textContent) : el.name || 'this field';
}

function rulesFor(el: FormControl): FieldRules {
  const isInput = el instanceof HTMLInputElement;
  const kind: FieldRules['kind'] =
    el instanceof HTMLSelectElement
      ? 'select'
      : isInput && el.type === 'checkbox'
        ? 'checkbox'
        : isInput && el.type === 'email'
          ? 'email'
          : 'text';
  return {
    kind,
    label: fieldLabel(el),
    required: el.required,
    workEmail: el.hasAttribute('data-work'),
    minLength: Number(el.dataset.min) || 0,
    maxLength: el instanceof HTMLSelectElement ? 0 : el.maxLength,
    checked: isInput ? el.checked : false,
  };
}

export function validateField(el: FormControl): string {
  const message = validateValue(el.value, rulesFor(el));
  showFieldError(el, message);
  return message;
}

export function validateGroup(fieldset: HTMLElement): string {
  const message = validateGroupSelection(
    $$('input[type="checkbox"]', fieldset, HTMLInputElement).filter((c) => c.checked).length,
  );
  showFieldError(fieldset, message);
  return message;
}

export const legendText = (fieldset: HTMLElement): string => cleanLabel($('legend', fieldset)?.textContent ?? null);
